import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from '@/shared/api/axios';
import { clearSession } from './userSlice';

// 列表請求途中若有本地更新（WS 訊息、送出訊息等，見 roomsVersion），回應可能是更新前的快照，
// 套用會把剛累加的未讀數、最新訊息蓋回去，所以重抓。重抓到上限仍有更新就套用最後一次的回應：
// 不能不套用，本次 session 第一次同步（roomsSynced）也走這裡，不套用的話聊天頁會一直等不到列表
const ROOM_LIST_MAX_ATTEMPTS = 3;
// 每送出一次列表請求就遞增：多支列表請求同時在途時（換頁、WS 重連、訂閱後各抓一次），
// 較早送出的回應較晚回來，不能蓋掉較新的
let roomListSeq = 0;

// 取得聊天室列表（目前選中偶像的）
// idolId 從 store 讀，不由呼叫端傳入（與 fetchNotifications 一致）：App 的 effect 閉包可能是舊值，
// 例如後台重導（AuthRedirect）同一次 commit 內換了偶像，掛載 effect 讀到的仍是上一個使用者的偶像
export const fetchRoomList = createAsyncThunk(
  'chat/fetchRoomList',
  async (_, { getState, rejectWithValue }) => {
    // 請求途中登出、換帳號（clearSession）時，回應屬於上一次登入：不套用、不重抓，
    // 否則會把上一個使用者的列表寫回已清空的 state 與 localStorage
    const sessionToken = getState().user.token;
    try {
      for (let attempt = 1; ; attempt++) {
        const versionAtRequest = getState().chat.roomsVersion;
        const seq = ++roomListSeq;
        const response = await Axios.post('/chat/get-room-list', {
          sender_idol_id: getState().user.idolId,
        });
        if (getState().user.token !== sessionToken) return { sessionChanged: true };

        if (response.data?.status_code !== 'SYSTEM_1000') {
          // 回應沒有 status_code（body 不是 JSON、是 null 等），error 會是 undefined：在這裡印出
          if (!response.data?.status_code) {
            console.error('Fetch room list error: unexpected response', response.data);
          }
          return rejectWithValue(response.data?.status_code);
        }
        if (getState().chat.roomsVersion === versionAtRequest || attempt === ROOM_LIST_MAX_ATTEMPTS) {
          return {
            rooms: response.data.data,
            imageUrl: response.data.image_url || null,
            seq,
          };
        }
      }
    } catch (error) {
      // 非 HTTP 例外（程式錯誤）沒有 httpErrorCode，error 會是 undefined：在這裡印出，避免被當成沒有錯誤
      if (!error.httpErrorCode) {
        console.error('Fetch room list error:', error);
      }
      if (getState().user.token !== sessionToken) return { sessionChanged: true };
      return rejectWithValue(error.httpErrorCode);
    }
  }
);

// 只有 rooms、imageUrl 會持久化（見 store.js 的 chatPersistConfig），其餘都是本次 session 的暫態
const initialState = {
  rooms: null,
  imageUrl: null,
  loading: false,
  error: null,
  // 本次 session 是否已成功抓過聊天室列表。false 時 rooms 是 localStorage 還原的舊列表，
  // 不能拿來判斷聊天室是否存在（後端可能已沒有該聊天室，或有新的聊天室）
  roomsSynced: false,
  // 聊天室列表的本地更新次數（WS 訊息、送出訊息、webhook 切換等）：fetchRoomList 據此判斷回應是不是更新前的快照
  roomsVersion: 0,
  // 已套用的列表回應中，最新一次請求的序號（見 fetchRoomList 的 roomListSeq）
  roomListAppliedSeq: 0,
  // ChatPage 目前開著的聊天對象 idol id（沒開聊天室時為 null）：收到 WS 訊息時據此判斷要交給 ChatPage 還是累加未讀
  currentChatIdolId: null,
  // 用於即時傳遞新訊息到 ChatPage（避免重複訂閱 WebSocket）
  incomingMessage: null,
  // 用於「對方正在輸入」功能
  typingIdolId: null,
};

// 聊天室的最新訊息更新後移到列表最上方；回傳是否找到該聊天室
const moveRoomToTop = (state, receiverIdolId, changes) => {
  const index = state.rooms ? state.rooms.findIndex(room => room.receiver_idol_id === receiverIdolId) : -1;
  if (index === -1) return false;
  const [room] = state.rooms.splice(index, 1);
  state.rooms.unshift({ ...room, ...changes });
  return true;
};

// 聊天室列表的寫入一律在 reducer 內以當下 state 計算，不由元件拿閉包裡的列表整包覆寫：
// 元件持有的列表可能已過期，整包寫回會蓋掉期間 WS 進來的未讀數
const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setCurrentChatIdolId(state, action) {
      state.currentChatIdolId = action.payload;
    },
    // App.jsx 收到 WS MessageSent 時 dispatch
    messageReceived(state, action) {
      state.roomsVersion += 1;
      const message = action.payload;
      const senderIdolId = Number(message.sender_idol_id);
      const preview = {
        message: message.message,
        message_type: message.message_type,
        created_at: message.created_at,
      };

      // 當前聊天對象的訊息交給 ChatPage 顯示，正在看所以沒有未讀
      if (senderIdolId === state.currentChatIdolId) {
        state.incomingMessage = message;
        moveRoomToTop(state, senderIdolId, { ...preview, is_read_count: 0 });
        return;
      }

      const index = state.rooms ? state.rooms.findIndex(room => room.receiver_idol_id === senderIdolId) : -1;
      if (index !== -1) {
        moveRoomToTop(state, senderIdolId, { ...preview, is_read_count: state.rooms[index].is_read_count + 1 });
      } else {
        const newRoom = {
          ...preview,
          receiver_idol_id: senderIdolId,
          receiver_user_id: message.sender_user_id,
          receiver_idol: message.friend_idol,
          is_read_count: 1,
        };
        state.rooms = state.rooms ? [newRoom, ...state.rooms] : [newRoom];
      }
    },
    // ChatPage 送出訊息成功後 dispatch；聊天室不在列表中（第一次傳訊）時新增
    messageSent(state, action) {
      state.roomsVersion += 1;
      const { receiverIdolId, receiverUserId, receiverIdol, message, messageType, createdAt } = action.payload;
      const preview = { message, message_type: messageType, created_at: createdAt };
      if (moveRoomToTop(state, receiverIdolId, preview)) return;
      const newRoom = {
        ...preview,
        receiver_idol_id: receiverIdolId,
        receiver_user_id: receiverUserId,
        receiver_idol: receiverIdol,
        is_read_count: 0,
      };
      state.rooms = state.rooms ? [newRoom, ...state.rooms] : [newRoom];
    },
    // ChatPage 載入聊天室訊息後 dispatch（後端讀取訊息即標記已讀）
    roomRead(state, action) {
      // 開著的聊天室在列表回應套用時本來就歸 0（見 fetchRoomList.fulfilled），不必讓途中的列表請求重抓
      if (action.payload !== state.currentChatIdolId) state.roomsVersion += 1;
      const room = state.rooms?.find(r => r.receiver_idol_id === action.payload);
      if (room) room.is_read_count = 0;
    },
    roomWebhookPausedChanged(state, action) {
      state.roomsVersion += 1;
      const { receiverIdolId, webhookPaused } = action.payload;
      const room = state.rooms?.find(r => r.receiver_idol_id === receiverIdolId);
      if (room) room.webhook_paused = webhookPaused;
    },
    clearIncomingMessage(state) {
      state.incomingMessage = null;
    },
    // 當收到對方正在輸入事件時，App.jsx 會 dispatch 這個 action
    setTypingIdolId(state, action) {
      state.typingIdolId = action.payload;
    },
    clearTypingIdolId(state) {
      state.typingIdolId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoomList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoomList.fulfilled, (state, action) => {
        if (action.payload.sessionChanged) return;
        // 較新的請求已經套用過：這是較早送出、較晚回來的回應
        if (action.payload.seq < state.roomListAppliedSeq) return;
        state.roomListAppliedSeq = action.payload.seq;
        state.loading = false;
        state.roomsSynced = true;
        // 開著的聊天室一律沒有未讀：列表請求可能比 ChatPage 載入訊息（後端標記已讀）先送出、晚回來，
        // 回應裡仍是標記前的未讀數
        state.rooms = action.payload.rooms.map(room =>
          room.receiver_idol_id === state.currentChatIdolId ? { ...room, is_read_count: 0 } : room
        );
        state.imageUrl = action.payload.imageUrl;
      })
      .addCase(fetchRoomList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // 登出／帳號停權／SYSTEM_1004／後台重導時清空：聊天室列表會持久化在 localStorage，不清會留給下一個使用同一台裝置的人
      .addCase(clearSession, () => initialState);
  },
});

export const {
  setCurrentChatIdolId,
  messageReceived,
  messageSent,
  roomRead,
  roomWebhookPausedChanged,
  clearIncomingMessage,
  setTypingIdolId,
  clearTypingIdolId,
} = chatSlice.actions;
export default chatSlice.reducer;
